// actions/authActions.js
import { setLocalStorageOnUserAuthentication, clearLocalStorageOnUserLogout, makeAuthenticatedRequest } from './authHelpers';
import { setUserData, setDataLoadingStatus } from '../slices/user.slice';
import { fetchPortfoliosInBatches } from '../../api/portfolioApi';
import { fetchUpcomingEvents, fetchTickerNews } from '../../api/tickersApi';
import { HOSTNAME } from '../../configs/Domain';

// Create loading status constants
const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

// Separate async data fetching into its own action
const fetchUserDataAsync = (userData) => async (dispatch) => {
  try {
    dispatch(setDataLoadingStatus({ type: 'portfolios', status: LOADING_STATES.LOADING }));
    dispatch(setDataLoadingStatus({ type: 'events', status: LOADING_STATES.LOADING }));
    dispatch(setDataLoadingStatus({ type: 'news', status: LOADING_STATES.LOADING }));


    // Use Promise.all to fetch data concurrently
    const promises = [];

    if (userData?.portfolios_v2) {
      const portfolioIds = userData.portfolios_v2.map(p => p.id);
      promises.push(
        dispatch(fetchPortfoliosInBatches({
          userId: userData.uid,
          portfolioIds,
          userObjects: userData.portfolios_v2,
        })).then(() => {
          dispatch(setDataLoadingStatus({ type: 'portfolios', status: LOADING_STATES.SUCCESS }));
        })
      );
    }

    if (userData?.tickers) {
      promises.push(
        dispatch(fetchUpcomingEvents(userData.tickers?.length>0 ? userData.tickers : ['spy']))
          .then(() => {
            dispatch(setDataLoadingStatus({ type: 'events', status: LOADING_STATES.SUCCESS }));
          })
      );

      promises.push(
        dispatch(fetchTickerNews(userData.tickers))
          .then(() => {
            dispatch(setDataLoadingStatus({ type: 'news', status: LOADING_STATES.SUCCESS }));
          })
      );
    }

    // Wait for all promises to settle (not blocking)
    Promise.allSettled(promises).catch((error) => {
      console.error('Error fetching user data:', error);
    });

  } catch (error) {
    console.error('Error initiating data fetch:', error);
    dispatch(setDataLoadingStatus({ type: 'portfolios', status: LOADING_STATES.ERROR }));
    dispatch(setDataLoadingStatus({ type: 'events', status: LOADING_STATES.ERROR }));
    dispatch(setDataLoadingStatus({ type: 'news', status: LOADING_STATES.ERROR }));
  }
};

export const login = (username, password, setIsLoggedIn, setLoginFailed) => async (dispatch) => {
  try {
    const response = await fetch(`${HOSTNAME}/auth/login/credentials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ u: username, p: password }),
    });

    const data = await response.json();
    if (response.status !== 200) {
      setLoginFailed(true);
      setIsLoggedIn(false);
      throw new Error(data.message || 'Login failed');
    }

    setIsLoggedIn(true);
    const userData = data.data;

    // Set initial data immediately
    setLocalStorageOnUserAuthentication(data.token, userData);
    dispatch(setUserData(userData));

    // Initiate background data loading
    dispatch(fetchUserDataAsync(userData));

  } catch (error) {
    console.error('Authentication failed:', error);
  }
};

export const checkIfAuthorized = () => async (dispatch) => {
  try {
    const data = await makeAuthenticatedRequest(`${HOSTNAME}/auth/validate`);

    const userData = data.user;
    setLocalStorageOnUserAuthentication(data.token, userData);
    dispatch(setUserData(userData));

    // Initiate background data loading
    dispatch(fetchUserDataAsync(userData));

    return true;
  } catch (error) {
    console.error('Authorization check failed:', error);
    clearLocalStorageOnUserLogout();
    return false;
  }
};

export const logout = () => async (dispatch) => {
  try {
    await fetch(`${HOSTNAME}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });

    clearLocalStorageOnUserLogout();
    dispatch(setUserData(null));

    // Reset all loading states
    dispatch(setDataLoadingStatus({ type: 'portfolios', status: LOADING_STATES.IDLE }));
    dispatch(setDataLoadingStatus({ type: 'events', status: LOADING_STATES.IDLE }));
    dispatch(setDataLoadingStatus({ type: 'news', status: LOADING_STATES.IDLE }));

  } catch (error) {
    console.error('Logout failed:', error);
  }
};